import { Tab, Tabs } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { useContext } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { KnownModule } from '../../api/user/Module';
import { UserContext } from '../../contexts/user-context';
import { Pages } from '../../router/constants';
import { COLORS } from '../../theme/variables';
import { checkDynamicPath } from '../../utils/functions';

const useStyles = makeStyles({
  container: {
    padding: '0 40px',
    boxShadow: `0px 1px 0px ${COLORS.COLOR_GRAY_LIGHTENED_30}`,
  },
  scrollContainer: {
    '& .MuiTabs-scroller': {
      overflowX: 'auto !important',
      '-ms-overflow-style': 'none',
      'scrollbar-width': 'none',

      '&::-webkit-scrollbar': {
        display: 'none',
      },
    },
  },
});

const checkIfSettings = (pathname: string) => {
  const settingsPages = [
    Pages.TENANT_SETTINGS,
    Pages.TENANT_QUESTIONS,
    Pages.TENANT_COHORTS,
    Pages.TENANT_AUTOMATION,
    Pages.TENANT_CUSTOM_SPECIALIZATIONS,
    Pages.TENANT_NOTIFICATION_SETTINGS,
    Pages.TENANT_MATCHING_WEIGHTS,
  ];

  return settingsPages.some((p) => p === pathname)
    ? pathname
    : Pages.TENANT_SETTINGS;
};

const checkIfForms = (pathname: string) => {
  if (
    checkDynamicPath(pathname, Pages.TENANT_FORMS_EDIT) ||
    checkDynamicPath(pathname, Pages.TENANT_CUSTOM_QUESTIONS_EDIT)
  ) {
    return pathname;
  }
  const formsPages = [
    Pages.TENANT_FORMS,
    Pages.TENANT_FORMS_LEAD_MENTOR_REPORT,
    Pages.TENANT_FORM_FOUNDER_NOTES_REPORT,
  ];

  return formsPages.some((p) => p === pathname) ? pathname : Pages.TENANT_FORMS;
};

const checkIfConsents = (pathname: string) => {
  const consentsPages = [
    Pages.TENANT_CONSENTS,
    Pages.TENANT_CONSENTS_MENTORS,
    Pages.TENANT_CONSENTS_FOUNDERS,
  ];
  return consentsPages.includes(pathname as Pages)
    ? (pathname as Pages)
    : Pages.TENANT_CONSENTS;
};

function PageTabs() {
  const location = useLocation();
  const classes = useStyles();
  const { hasAccessToAction, hasAccessToModal } = useContext(UserContext);

  // This is a hack to make settings tab active both on settings and questions page,
  // because Material-UI Tabs work by static values instead of boolean expressions.
  const selectedSettings = checkIfSettings(location.pathname);

  const selectedForms = checkIfForms(location.pathname);
  const selectedConsents = checkIfConsents(location.pathname);

  return (
    <div className={classes.container}>
      <Tabs
        value={location.pathname}
        indicatorColor='primary'
        textColor='primary'
        className={classes.scrollContainer}
      >
        {hasAccessToAction('edit') && (
          <Tab
            data-testid='tenant-nav-profile'
            value={Pages.TENANT_PROFILE}
            label='Profile'
            component={Link}
            to={Pages.TENANT_PROFILE}
          />
        )}
        <Tab
          data-testid='tenant-nav-account'
          value={Pages.TENANT_ACCOUNT}
          label='Account'
          component={Link}
          to={Pages.TENANT_ACCOUNT}
        />
        {hasAccessToAction('settings.view') && (
          <Tab
            data-testid='tenant-nav-settings'
            value={selectedSettings}
            label='Settings'
            component={Link}
            to={Pages.TENANT_SETTINGS}
          />
        )}
        {hasAccessToAction('channels.view') && (
          <Tab
            data-testid='tenant-nav-channels'
            value={Pages.TENANT_CHANNELS}
            label='Channels'
            component={Link}
            to={Pages.TENANT_CHANNELS}
          />
        )}
        {hasAccessToAction('userManagement.view') && (
          <Tab
            data-testid='tenant-nav-user-management'
            value={Pages.TENANT_USER_MANAGEMENT}
            label='User Management'
            component={Link}
            to={Pages.TENANT_USER_MANAGEMENT}
          />
        )}
        {hasAccessToAction('modules.enrollment.view') &&
          hasAccessToModal(KnownModule.Enrollment) && (
            <Tab
              data-testid='tenant-nav-forms'
              value={selectedForms}
              label='Forms'
              component={Link}
              to={Pages.TENANT_FORMS}
            />
          )}
        {hasAccessToAction('consents.view') && (
          <Tab
            data-testid='tenant-nav-consents'
            value={selectedConsents}
            label='Commitments'
            component={Link}
            to={Pages.TENANT_CONSENTS}
          />
        )}
        {hasAccessToAction('settings.notificationSettings.view') &&
          hasAccessToAction('settings.notificationSettings.tab') && (
            <Tab
              data-testid='tenant-nav-notification-settings'
              value={Pages.TENANT_NOTIFICATION_SETTINGS}
              label='Notification Settings'
              component={Link}
              to={Pages.TENANT_NOTIFICATION_SETTINGS}
            />
          )}
      </Tabs>
    </div>
  );
}

export default PageTabs;
